import { PropsWithRef, useEffect, useRef } from 'react';
import { InputDropZoneProps } from './types';
import { Typography } from '../typography/typography';
import { Content, StyledInput, Wrapper } from './input-drop-zone.styles';
import { createFileData } from './create-file-data';
import { IconAttach } from '@shared/icon/icon-attach/icon-attach';

export const InputDropZone = ({
    additionalPlaceholder = 'файл',
    accept,
    name,
    placeholder,
    max,
    disabled,
    onChange,
}: PropsWithRef<InputDropZoneProps>) => {
    const containerRef = useRef<HTMLLabelElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = e.target;
        if (target && onChange) {
            const fileData = await createFileData(target.files);
            onChange(fileData);
        }
    };

    useEffect(() => {
        const container = containerRef.current;
        const input = inputRef.current;
        const handlePreventDefault = (e: DragEvent) => {
            e.preventDefault();
        };
        const handleDrop = async (e: DragEvent) => {
            const files = e?.dataTransfer?.files || null;
            handlePreventDefault(e);
            e.stopPropagation();
            if (input) {
                input.files = files;
            }
            if (onChange) {
                const fileData = await createFileData(files);
                onChange(fileData);
            }
        };
        if (container) {
            container.addEventListener('dragover', handlePreventDefault, false);
            container.addEventListener('dragenter', handlePreventDefault);
            container.addEventListener('drop', handleDrop);
        }

        return () => {
            if (container) {
                container.removeEventListener('dragover', handlePreventDefault);
                container.removeEventListener('dragenter', handlePreventDefault);
                container.removeEventListener('drop', handleDrop);
            }
        };
    }, [containerRef.current, inputRef.current]);

    return (
        <Wrapper htmlFor={name} ref={containerRef} $disabled={Boolean(disabled)}>
            <Content>
                <IconAttach />
                {!placeholder && (
                    <Typography size='body2_long'>
                        Перетащите в эту область или <Typography color='bright_blue'>загрузите</Typography>{' '}
                        {additionalPlaceholder}
                    </Typography>
                )}
                {placeholder}
            </Content>
            <StyledInput
                ref={inputRef}
                disabled={disabled}
                type='file'
                multiple={max !== 1}
                max={max}
                accept={accept}
                onChange={handleChange}
            />
        </Wrapper>
    );
};

InputDropZone.displayName = 'InputFileSearch';
